import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LogOut } from "lucide-react";
import TitleBar from "../components/TitleBar";
import { Helmet } from "react-helmet-async";
import { Stat } from "../components/plan/shared";
import { useToast } from "../hooks/use-toast";
import { StudioGate } from "../components/studio/StudioGate";
import { StudioGenerator } from "../components/studio/StudioGenerator";
import { GuidesPanel } from "../components/studio/GuidesPanel";
import { IgConnectPanel } from "../components/studio/IgConnectPanel";
import { PostCard } from "../components/studio/PostCard";
import { studioApi, getOwnerCode, setOwnerCode, errMsg } from "../lib/studioApi";

const TABS = [["upcoming", "Drafts"], ["posted", "Posted"], ["all", "All"]];
const WEEKLY_TARGET = 4;

const weekBounds = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const start = d.toISOString().slice(0, 10);
  d.setDate(d.getDate() + 6);
  return [start, d.toISOString().slice(0, 10)];
};

const sortPosts = (a, b) => (a.scheduled_for || "9999").localeCompare(b.scheduled_for || "9999");

export default function Studio() {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(!!getOwnerCode());
  const [posts, setPosts] = useState([]);
  const [themes, setThemes] = useState([]);
  const [ig, setIg] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const { toast } = useToast();
  const notify = useCallback((title, variant) => toast({ title, variant }), [toast]);

  const load = useCallback(async () => setPosts(await studioApi.list()), []);

  useEffect(() => {
    if (!getOwnerCode()) return;
    studioApi.verify().then(() => setUnlocked(true)).catch(() => setOwnerCode("")).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (!unlocked || !q.get("ig")) return;
    if (q.get("ig") === "connected") notify("Instagram connected — auto-publish is live");
    else notify(`Instagram: ${q.get("msg") || "connection failed"}`, "destructive");
    window.history.replaceState({}, "", "/studio");
  }, [unlocked, notify]);

  useEffect(() => {
    if (!unlocked) return;
    load();
    studioApi.themes().then(setThemes);
    studioApi.igStatus().then(setIg).catch(() => setIg({ configured: false }));
  }, [unlocked, load]);

  const generating = posts.some((p) =>
    p.image_status === "generating" || p.reel_status === "rendering" ||
    (p.image_status === "ready" && !p.reel_status && p.status !== "posted" && p.scheduled_for >= new Date().toISOString().slice(0, 10)) ||
    Object.entries(p.ig || {}).some(([k, v]) => k.endsWith("_status") && v === "publishing"));
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [generating, load]);

  const act = (fn, okMsg) => async (...args) => {
    try {
      await fn(...args);
      if (okMsg) notify(okMsg);
      await load();
    } catch (e) {
      notify(errMsg(e), "destructive");
    }
  };

  const stats = useMemo(() => {
    const [s, e] = weekBounds();
    const week = posts.filter((p) => p.scheduled_for >= s && p.scheduled_for <= e);
    return { planned: week.length, posted: week.filter((p) => p.status === "posted").length, total: posts.length };
  }, [posts]);

  const visible = posts
    .filter((p) => tab === "all" || (tab === "posted" ? p.status === "posted" : p.status !== "posted"))
    .sort(sortPosts);

  const logout = () => { setOwnerCode(""); setUnlocked(false); setPosts([]); };

  return (
    <div className="min-h-screen bg-[#0b0b0c] text-white">
      <Helmet><title>Content Studio · FreeFitFuel</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      <TitleBar
        title="Content Studio"
        eyebrow="Founder · Instagram"
        back="/settings"
        rightSlot={unlocked ? (
          <button onClick={logout} aria-label="Lock studio" data-testid="studio-lock-btn" className="h-9 w-9 rounded-full grid place-items-center text-zinc-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4" />
          </button>
        ) : <span />}
      />
      {checking ? null : !unlocked ? (
        <StudioGate onUnlock={() => setUnlocked(true)} />
      ) : (
        <main data-testid="studio-page" className="max-w-[1180px] mx-auto px-4 lg:px-8 py-8 flex flex-col gap-6 pb-24">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#BEFF38]">Grow · Advertise · Promote health</p>
            <h1 className="text-3xl lg:text-4xl font-black uppercase mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Instagram content studio
            </h1>
          </div>
          <div className="grid grid-cols-3 gap-3" data-testid="studio-stats">
            <Stat label="This week" value={`${stats.planned}/${WEEKLY_TARGET}`} sub="posts planned" />
            <Stat label="Posted" value={`${stats.posted}/${WEEKLY_TARGET}`} sub="this week" />
            <Stat label="Library" value={stats.total} sub="total posts" />
          </div>
          <IgConnectPanel status={ig} onChange={setIg} notify={notify} />
          <GuidesPanel notify={notify} />
          <StudioGenerator
            themes={themes}
            onWeek={act(async (b) => {
              const r = await studioApi.generateWeek(b);
              notify(r.failed ? `${r.posts.length} posts written · ${r.failed} failed — add it as a single post` : "4 guides + Niggle of the Week written — designing now");
            })}
            onSingle={act((b) => studioApi.generate(b), "Post written — photo is on the way")}
          />
          <div className="flex gap-2" data-testid="studio-tabs">
            {TABS.map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`studio-tab-${id}`}
                className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${
                  tab === id ? "bg-[#BEFF38] text-black" : "bg-zinc-900 text-zinc-400 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {visible.length === 0 ? (
            <p data-testid="studio-empty" className="text-zinc-500 text-sm italic">No posts here yet — generate a week to get started.</p>
          ) : (
            <div className="flex flex-col gap-4" data-testid="studio-post-list">
              {visible.map((p) => (
                <PostCard
                  key={p.id}
                  post={p}
                  igConnected={!!ig?.connected}
                  notify={notify}
                  onUpdate={act((b) => studioApi.update(p.id, b), "Saved")}
                  onRegen={act(() => studioApi.regenerateImage(p.id), "Creating a new photo…")}
                  onDelete={act(() => studioApi.remove(p.id), "Post deleted")}
                  onPublish={act((f) => studioApi.publish(p.id, f), "Publishing to Instagram…")}
                  onReel={act(() => studioApi.createReel(p.id), "Building your reel…")}
                />
              ))}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
